import React from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/layout/Navbar';
import Header from '@/components/layout/Header';
import { Button } from '@/components/ui/button';
import { useCourseEditor } from '@/hooks/useCourseEditor';
import { CourseEditorContent } from './course-editor';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { useAuthRedirect } from '@/hooks/useAuthRedirect';

const CourseEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = React.useState<string | null>(null);
  
  // Determine if this is a new course from the URL path
  const isNewCoursePath = location.pathname.endsWith('/new');
  const courseId = isNewCoursePath ? 'new' : id;
  
  // Use the auth redirect hook
  useAuthRedirect(false);
  
  // Effect to handle admin check after authentication
  React.useEffect(() => {
    if (isAuthenticated && user && user.role !== 'admin') {
      // User is authenticated but not an admin
      setError("You must be an administrator to access this page");
    } else if (isAuthenticated && !user) {
      // Authenticated but user object is not yet available, wait...
    } else if (!isAuthenticated) {
      // Hook is handling the redirect, clear any previous error
      setError(null);
    }
  }, [isAuthenticated, user]); // Depend on auth state

  const {
    lessons,
    setLessons,
    isLoading,
    isSaving,
    initialFormValues,
    isNewCourse,
    handleSubmit,
    handleDeleteCourse
  } = useCourseEditor(courseId, user?.id);

  // Don't render anything during authentication check - MODIFY THIS
  // Render loading or null while auth is resolving
  if (isLoading && !error) {
    // Show a loading indicator while fetching course data
    return (
      <div className="flex min-h-screen flex-col items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
        <p className="mt-4 text-muted-foreground">Loading editor...</p>
      </div>
    );
  }
  
  // Show error message if access is denied
  if (error) {
    return (
      <div className="flex min-h-screen flex-col">
        <Navbar />
        <main className="flex-1 container py-6">
          <Alert variant="destructive">
            <AlertTitle>Access Denied</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <div className="mt-4">
            <Button variant="ghost" asChild>
              <Link to="/dashboard">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Dashboard
              </Link>
            </Button>
          </div>
        </main>
      </div>
    );
  }
  
  // Don't render anything during authentication check
  if (!isAuthenticated || user?.role !== 'admin') {
    return null;
  }
  
  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      
      <main className="flex-1 container py-6">
        <div className="mb-6">
          <Button variant="ghost" asChild className="mb-4">
            <Link to="/admin/dashboard" className="inline-flex items-center">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Link>
          </Button>
          
          <Header 
            title={isNewCourse ? "Create New Course" : "Edit Course"} 
            description={isNewCourse 
              ? "Add details for your new course" 
              : "Update your course information and manage lessons"
            } 
          />
        </div>
        
        <CourseEditorContent 
          isLoading={isLoading}
          isNewCourse={isNewCourse}
          courseId={courseId || ''}
          lessons={lessons}
          setLessons={setLessons}
          initialFormValues={initialFormValues}
          isSaving={isSaving}
          onSubmit={handleSubmit}
          onDelete={handleDeleteCourse}
        />
      </main>
    </div>
  );
};

export default CourseEditor;
